'use client';

/**
 * ─── MOTOR FINANCIERO CENTRALIZADO — PRESTAMASS ─────────────────────────────
 * FUENTE ÚNICA DE VERDAD FINANCIERA
 * Todos los módulos deben usar estas funciones. NUNCA calcular de forma independiente.
 *
 * REGLA DE ORO:
 * Capital Prestado = Dinero realmente desembolsado (NUNCA incluye interés/servicio/mora/multa)
 * Capital Recuperado = Capital efectivamente aplicado mediante pagos
 * Capital Pendiente = Capital Prestado - Capital Recuperado
 */

import type { Prestamo, Cuota, Pago, Cliente } from '@/lib/dataStore';

// ─── Lazy import to avoid circular dependency ─────────────────────────────────
// loanStatusEngine imports from financialEngine, so we use a lazy approach here
function getCarteraEnMora(prestamos: Prestamo[]): number {
  // A loan is "en mora" if it has overdue installments and balance > 0
  // We check this without importing loanStatusEngine to avoid circular deps
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  return prestamos
    .filter(p => {
      if (p.estado === 'refinanciado' || p.estado === 'cancelado' || p.estado === 'pagado') return false;
      // Skip migrated loans with mora disabled
      if ((p as any).moraDesactivada) return false;
      const totalPend = calculateTotalPending(p);
      if (totalPend <= 0.01) return false;
      // Check if any cuota is overdue
      const cuotasPendientes = p.cuotas.filter(c => c.estado !== 'pagado' && c.estado !== 'cancelado');
      if (cuotasPendientes.length === 0) return true; // mora/multas remaining
      const proxima = cuotasPendientes.sort((a, b) => a.numero - b.numero)[0];
      if (!proxima.fechaVencimiento) return false;
      let fechaVenc: Date;
      if (proxima.fechaVencimiento.includes('/')) {
        const [d, m, y] = proxima.fechaVencimiento.split('/').map(Number);
        fechaVenc = new Date(y, m - 1, d);
      } else {
        const [y, m, d] = proxima.fechaVencimiento.split('-').map(Number);
        fechaVenc = new Date(y, m - 1, d);
      }
      return hoy > fechaVenc;
    })
    .reduce((s, p) => {
      const sf = calculateSaldoFinanciero(p);
      return s + sf.capitalPendiente;
    }, 0);
}

// ─── Tipos de Saldo Desglosado ────────────────────────────────────────────────

export interface SaldoFinanciero {
  // Capital
  capitalPrestado: number;       // Dinero realmente desembolsado
  capitalRecuperado: number;     // Capital efectivamente pagado
  capitalPendiente: number;      // Capital que falta pagar

  // Interés
  interesTotal: number;          // Interés total del préstamo
  interesRecuperado: number;     // Interés efectivamente cobrado
  interesPendiente: number;      // Interés que falta cobrar

  // Servicio Administrativo
  servicioTotal: number;         // Servicio admin total
  servicioRecuperado: number;    // Servicio admin cobrado
  servicioPendiente: number;     // Servicio admin pendiente

  // Mora
  moraGenerada: number;          // Mora total generada
  moraRecuperada: number;        // Mora efectivamente cobrada
  moraPendiente: number;         // Mora pendiente

  // Multas
  multaGenerada: number;         // Multa total generada
  multaRecuperada: number;       // Multa efectivamente cobrada
  multaPendiente: number;        // Multa pendiente

  // Totales
  totalRecaudado: number;        // Total de dinero recibido (capital+interés+servicio+mora+multa)
  totalPendiente: number;        // Total pendiente de cobro
  saldoAFavor: number;           // Excedente si hay sobrepago (nunca negativo)
}

export interface FactorRiesgo {
  factor: string;
  descripcion: string;
  impacto: number; // puntos que resta del score
  valor: string | number;
}

export interface PerfilRiesgo {
  clienteId: string;
  score: number;                 // 0-100
  nivel: NivelRiesgo;
  factores: FactorRiesgo[];
  tendencia: 'mejorando' | 'estable' | 'empeorando';
  tendenciaDescripcion: string;
  recomendacion: string;
  // Historial
  prestamosRealizados: number;
  prestamosActivos: number;
  prestamosPagados: number;
  prestamosVencidos: number;
  prestamosRefinanciados: number;
  // Finanzas
  capitalPrestadoHistorico: number;
  capitalRecuperado: number;
  capitalPendiente: number;
  interesRecuperado: number;
  totalRecaudado: number;
  moraTotal: number;
  multaTotal: number;
  // Comportamiento
  pagosPuntuales: number;
  pagosAtrasados: number;
  pagosParciales: number;
  mayorAtraso: number;
  promedioAtraso: number;
  ultimoPago: string;
  fechaCalculo: string;
}

export type NivelRiesgo = 'sin-historial' | 'bajo' | 'medio' | 'alto' | 'critico';

export interface NivelRiesgoInfo {
  nivel: NivelRiesgo;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  emoji: string;
  scoreMin: number;
  scoreMax: number;
}

export const NIVELES_RIESGO: NivelRiesgoInfo[] = [
  { nivel: 'sin-historial', label: 'Sin Historial', color: 'text-gray-600', bgColor: 'bg-gray-100', borderColor: 'border-gray-300', emoji: '⚪', scoreMin: 0, scoreMax: 100 },
  { nivel: 'bajo',          label: 'Bajo',          color: 'text-green-700', bgColor: 'bg-green-100', borderColor: 'border-green-300', emoji: '🟢', scoreMin: 80, scoreMax: 100 },
  { nivel: 'medio',         label: 'Medio',         color: 'text-yellow-700', bgColor: 'bg-yellow-100', borderColor: 'border-yellow-300', emoji: '🟡', scoreMin: 60, scoreMax: 79 },
  { nivel: 'alto',          label: 'Alto',          color: 'text-orange-700', bgColor: 'bg-orange-100', borderColor: 'border-orange-300', emoji: '🟠', scoreMin: 40, scoreMax: 59 },
  { nivel: 'critico',       label: 'Crítico',       color: 'text-red-700', bgColor: 'bg-red-100', borderColor: 'border-red-300', emoji: '🔴', scoreMin: 0, scoreMax: 39 },
];

export function getNivelRiesgoInfo(nivel: NivelRiesgo): NivelRiesgoInfo {
  return NIVELES_RIESGO.find(n => n.nivel === nivel) || NIVELES_RIESGO[0];
}

export function scoreToNivel(score: number, sinHistorial = false): NivelRiesgo {
  if (sinHistorial) return 'sin-historial';
  if (score >= 80) return 'bajo';
  if (score >= 60) return 'medio';
  if (score >= 40) return 'alto';
  return 'critico';
}

// ─── Redondeo financiero ──────────────────────────────────────────────────────
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ─── FUNCIÓN PRINCIPAL: Calcular saldo financiero completo de un préstamo ────

/**
 * calculateCapitalPending — Capital que falta pagar
 * Basado en capital original menos capital efectivamente aplicado en pagos.
 * NUNCA resta intereses, servicios, mora o multas del capital.
 */
export function calculateCapitalPending(prestamo: Prestamo): number {
  const capitalOriginal = prestamo.capital;
  const capitalPagado = prestamo.cuotas.reduce((s, c) => s + (c.capitalPagado || 0), 0);
  return r2(Math.max(0, capitalOriginal - capitalPagado));
}

/**
 * calculateInterestPending — Interés que falta cobrar
 */
export function calculateInterestPending(prestamo: Prestamo): number {
  const interesTotal = prestamo.interes;
  const interesPagado = prestamo.cuotas.reduce((s, c) => s + (c.interesPagado || 0), 0);
  return r2(Math.max(0, interesTotal - interesPagado));
}

/**
 * calculateAdministrativeFeePending — Servicio administrativo pendiente
 */
export function calculateAdministrativeFeePending(prestamo: Prestamo): number {
  const servicioTotal = prestamo.servicioAdmin * prestamo.cuotasTotal;
  const servicioPagado = prestamo.cuotas.reduce((s, c) => s + (c.servicioAdminPagado || 0), 0);
  return r2(Math.max(0, servicioTotal - servicioPagado));
}

/**
 * calculateLateFeePending — Mora pendiente
 */
export function calculateLateFeePending(prestamo: Prestamo): number {
  const moraTotal = prestamo.mora || 0;
  const moraPagada = prestamo.cuotas.reduce((s, c) => s + (c.moraPagada || 0), 0);
  return r2(Math.max(0, moraTotal - moraPagada));
}

/**
 * calculatePenaltyPending — Multas pendientes
 */
export function calculatePenaltyPending(prestamo: Prestamo): number {
  const multaTotal = prestamo.multa || 0;
  const multaPagada = prestamo.cuotas.reduce((s, c) => s + (c.multaPagada || 0), 0);
  return r2(Math.max(0, multaTotal - multaPagada));
}

/**
 * calculateTotalPending — Total pendiente (todos los conceptos)
 */
export function calculateTotalPending(prestamo: Prestamo): number {
  return r2(
    calculateCapitalPending(prestamo) +
    calculateInterestPending(prestamo) +
    calculateAdministrativeFeePending(prestamo) +
    calculateLateFeePending(prestamo) +
    calculatePenaltyPending(prestamo)
  );
}

/**
 * calculateSaldoFinanciero — Saldo financiero completo y desglosado de un préstamo
 * Esta es la función maestra que todos los módulos deben usar.
 */
export function calculateSaldoFinanciero(prestamo: Prestamo): SaldoFinanciero {
  // Capital
  const capitalPrestado = prestamo.capital;
  const capitalRecuperado = r2(prestamo.cuotas.reduce((s, c) => s + (c.capitalPagado || 0), 0));
  const capitalPendiente = r2(Math.max(0, capitalPrestado - capitalRecuperado));

  // Interés
  const interesTotal = prestamo.interes;
  const interesRecuperado = r2(prestamo.cuotas.reduce((s, c) => s + (c.interesPagado || 0), 0));
  const interesPendiente = r2(Math.max(0, interesTotal - interesRecuperado));

  // Servicio
  const servicioTotal = r2(prestamo.servicioAdmin * prestamo.cuotasTotal);
  const servicioRecuperado = r2(prestamo.cuotas.reduce((s, c) => s + (c.servicioAdminPagado || 0), 0));
  const servicioPendiente = r2(Math.max(0, servicioTotal - servicioRecuperado));

  // Mora
  const moraGenerada = prestamo.mora || 0;
  const moraRecuperada = r2(prestamo.cuotas.reduce((s, c) => s + (c.moraPagada || 0), 0));
  const moraPendiente = r2(Math.max(0, moraGenerada - moraRecuperada));

  // Multa
  const multaGenerada = prestamo.multa || 0;
  const multaRecuperada = r2(prestamo.cuotas.reduce((s, c) => s + (c.multaPagada || 0), 0));
  const multaPendiente = r2(Math.max(0, multaGenerada - multaRecuperada));

  // Totales
  const totalRecaudado = r2(capitalRecuperado + interesRecuperado + servicioRecuperado + moraRecuperada + multaRecuperada);
  const totalPendiente = r2(capitalPendiente + interesPendiente + servicioPendiente + moraPendiente + multaPendiente);

  // Saldo a favor (sobrepago)
  const totalPagadoCuotas = r2(prestamo.cuotas.reduce((s, c) => s + (c.montoPagado || 0), 0));
  const totalEsperado = r2(capitalPrestado + interesTotal + servicioTotal + moraGenerada + multaGenerada);
  const saldoAFavor = r2(Math.max(0, totalPagadoCuotas - totalEsperado));

  return {
    capitalPrestado,
    capitalRecuperado,
    capitalPendiente,
    interesTotal,
    interesRecuperado,
    interesPendiente,
    servicioTotal,
    servicioRecuperado,
    servicioPendiente,
    moraGenerada,
    moraRecuperada,
    moraPendiente,
    multaGenerada,
    multaRecuperada,
    multaPendiente,
    totalRecaudado,
    totalPendiente,
    saldoAFavor,
  };
}

// ─── Estadísticas Financieras Globales ───────────────────────────────────────

export interface EstadisticasFinancieras {
  // Capital
  capitalPrestadoTotal: number;      // Total capital desembolsado (SOLO capital, nunca intereses)
  capitalRecuperadoTotal: number;    // Capital efectivamente cobrado
  capitalPendienteTotal: number;     // Capital que falta cobrar

  // Interés
  interesRecuperadoTotal: number;    // Interés efectivamente cobrado
  interesPendienteTotal: number;     // Interés pendiente

  // Servicio
  servicioRecuperadoTotal: number;   // Servicio admin cobrado
  servicioPendienteTotal: number;    // Servicio admin pendiente

  // Mora
  moraRecuperadaTotal: number;       // Mora cobrada
  moraPendienteTotal: number;        // Mora pendiente

  // Multas
  multaRecuperadaTotal: number;      // Multas cobradas
  multaPendienteTotal: number;       // Multas pendientes

  // Totales
  totalRecaudado: number;            // Todo el dinero recibido
  carteraPendiente: number;          // Toda la cartera pendiente
  carteraEnMora: number;             // Cartera de préstamos en mora
}

export function calcularEstadisticasFinancieras(prestamos: Prestamo[]): EstadisticasFinancieras {
  let capitalPrestadoTotal = 0;
  let capitalRecuperadoTotal = 0;
  let interesRecuperadoTotal = 0;
  let servicioRecuperadoTotal = 0;
  let moraRecuperadaTotal = 0;
  let multaRecuperadaTotal = 0;
  let interesPendienteTotal = 0;
  let servicioPendienteTotal = 0;
  let moraPendienteTotal = 0;
  let multaPendienteTotal = 0;

  for (const p of prestamos) {
    if (p.estado === 'borrador') continue;
    const saldo = calculateSaldoFinanciero(p);
    capitalPrestadoTotal += p.capital; // SOLO capital desembolsado
    capitalRecuperadoTotal += saldo.capitalRecuperado;
    interesRecuperadoTotal += saldo.interesRecuperado;
    servicioRecuperadoTotal += saldo.servicioRecuperado;
    moraRecuperadaTotal += saldo.moraRecuperada;
    multaRecuperadaTotal += saldo.multaRecuperada;
    interesPendienteTotal += saldo.interesPendiente;
    servicioPendienteTotal += saldo.servicioPendiente;
    moraPendienteTotal += saldo.moraPendiente;
    multaPendienteTotal += saldo.multaPendiente;
  }

  const capitalPendienteTotal = r2(Math.max(0, capitalPrestadoTotal - capitalRecuperadoTotal));
  const totalRecaudado = r2(capitalRecuperadoTotal + interesRecuperadoTotal + servicioRecuperadoTotal + moraRecuperadaTotal + multaRecuperadaTotal);
  const carteraPendiente = r2(capitalPendienteTotal + interesPendienteTotal + servicioPendienteTotal + moraPendienteTotal + multaPendienteTotal);
  const carteraEnMora = r2(getCarteraEnMora(prestamos));

  return {
    capitalPrestadoTotal: r2(capitalPrestadoTotal),
    capitalRecuperadoTotal: r2(capitalRecuperadoTotal),
    capitalPendienteTotal,
    interesRecuperadoTotal: r2(interesRecuperadoTotal),
    interesPendienteTotal: r2(interesPendienteTotal),
    servicioRecuperadoTotal: r2(servicioRecuperadoTotal),
    servicioPendienteTotal: r2(servicioPendienteTotal),
    moraRecuperadaTotal: r2(moraRecuperadaTotal),
    moraPendienteTotal: r2(moraPendienteTotal),
    multaRecuperadaTotal: r2(multaRecuperadaTotal),
    multaPendienteTotal: r2(multaPendienteTotal),
    totalRecaudado,
    carteraPendiente,
    carteraEnMora,
  };
}

// ─── Conciliación Financiera ──────────────────────────────────────────────────

export type TipoInconsistencia =
  | 'distribucion-no-suma' |'capital-negativo' |'saldo-negativo' |'mora-duplicada' |'pago-sin-prestamo' |'cuota-sin-prestamo' |'prestamo-sin-plan' |'capital-recuperado-excede' |'pago-sin-distribucion' |'cuota-inconsistente';

export interface InconsistenciaFinanciera {
  id: string;
  tipo: TipoInconsistencia;
  severidad: 'error' | 'advertencia' | 'info';
  descripcion: string;
  prestamoId?: string;
  prestamoNumero?: string;
  clienteNombre?: string;
  cuotaId?: string;
  pagoId?: string;
  valorEsperado?: number;
  valorEncontrado?: number;
  diferencia?: number;
}

export function detectarInconsistencias(prestamos: Prestamo[], pagos: Pago[]): InconsistenciaFinanciera[] {
  const inconsistencias: InconsistenciaFinanciera[] = [];
  let idx = 0;

  for (const prestamo of prestamos) {
    if (prestamo.estado === 'borrador') continue;

    // 1. Préstamo sin cuotas
    if (!prestamo.cuotas || prestamo.cuotas.length === 0) {
      inconsistencias.push({
        id: `inc-${idx++}`,
        tipo: 'prestamo-sin-plan',
        severidad: 'error',
        descripcion: `Préstamo ${prestamo.numero} no tiene plan de pago (cuotas)`,
        prestamoId: prestamo.id,
        prestamoNumero: prestamo.numero,
        clienteNombre: prestamo.cliente,
      });
      continue;
    }

    // 2. Capital recuperado no puede exceder capital prestado
    const capitalRecuperado = prestamo.cuotas.reduce((s, c) => s + (c.capitalPagado || 0), 0);
    if (capitalRecuperado > prestamo.capital + 0.01) {
      inconsistencias.push({
        id: `inc-${idx++}`,
        tipo: 'capital-recuperado-excede',
        severidad: 'error',
        descripcion: `Capital recuperado (${r2(capitalRecuperado)}) excede capital prestado (${prestamo.capital}) en préstamo ${prestamo.numero}`,
        prestamoId: prestamo.id,
        prestamoNumero: prestamo.numero,
        clienteNombre: prestamo.cliente,
        valorEsperado: prestamo.capital,
        valorEncontrado: r2(capitalRecuperado),
        diferencia: r2(capitalRecuperado - prestamo.capital),
      });
    }

    // 3. Saldo negativo
    if (prestamo.saldo < -0.01) {
      inconsistencias.push({
        id: `inc-${idx++}`,
        tipo: 'saldo-negativo',
        severidad: 'error',
        descripcion: `Saldo negativo (${r2(prestamo.saldo)}) en préstamo ${prestamo.numero}`,
        prestamoId: prestamo.id,
        prestamoNumero: prestamo.numero,
        clienteNombre: prestamo.cliente,
        valorEncontrado: r2(prestamo.saldo),
      });
    }

    // 4. Verificar distribución de pagos en cuotas
    for (const cuota of prestamo.cuotas) {
      if (!cuota.pagos || cuota.pagos.length === 0) continue;

      for (const desglose of cuota.pagos) {
        const sumaDistribucion = r2(desglose.capital + desglose.interes + desglose.servicio + desglose.mora + desglose.multa);
        const diferencia = Math.abs(sumaDistribucion - desglose.montoTotal);
        if (diferencia > 0.02) {
          inconsistencias.push({
            id: `inc-${idx++}`,
            tipo: 'distribucion-no-suma',
            severidad: 'error',
            descripcion: `Distribución de pago ${desglose.numeroRecibo} no suma al total. Distribución: ${sumaDistribucion}, Total: ${desglose.montoTotal}`,
            prestamoId: prestamo.id,
            prestamoNumero: prestamo.numero,
            clienteNombre: prestamo.cliente,
            cuotaId: cuota.id,
            pagoId: desglose.pagoId,
            valorEsperado: desglose.montoTotal,
            valorEncontrado: sumaDistribucion,
            diferencia: r2(diferencia),
          });
        }
      }

      // 5. Cuota con montoPagado pero sin pagos registrados
      if ((cuota.montoPagado || 0) > 0 && (!cuota.pagos || cuota.pagos.length === 0)) {
        inconsistencias.push({
          id: `inc-${idx++}`,
          tipo: 'pago-sin-distribucion',
          severidad: 'advertencia',
          descripcion: `Cuota #${cuota.numero} del préstamo ${prestamo.numero} tiene monto pagado pero sin desglose de pagos`,
          prestamoId: prestamo.id,
          prestamoNumero: prestamo.numero,
          clienteNombre: prestamo.cliente,
          cuotaId: cuota.id,
          valorEncontrado: cuota.montoPagado,
        });
      }
    }

    // 6. Pagos del préstamo sin distribución
    const pagosPrestamo = pagos.filter(p => p.prestamoId === prestamo.id);
    for (const pago of pagosPrestamo) {
      const sumaConceptos = r2(pago.capital + pago.interes + pago.servicioAdmin + pago.mora + pago.multa);
      const diferencia = Math.abs(sumaConceptos - pago.total);
      if (diferencia > 0.02) {
        inconsistencias.push({
          id: `inc-${idx++}`,
          tipo: 'distribucion-no-suma',
          severidad: 'error',
          descripcion: `Pago ${pago.numeroRecibo} — suma de conceptos (${sumaConceptos}) ≠ total (${pago.total})`,
          prestamoId: prestamo.id,
          prestamoNumero: prestamo.numero,
          clienteNombre: prestamo.cliente,
          pagoId: pago.id,
          valorEsperado: pago.total,
          valorEncontrado: sumaConceptos,
          diferencia: r2(diferencia),
        });
      }
    }
  }

  // 7. Pagos sin préstamo asociado
  for (const pago of pagos) {
    const prestamoExiste = prestamos.some(p => p.id === pago.prestamoId);
    if (!prestamoExiste) {
      inconsistencias.push({
        id: `inc-${idx++}`,
        tipo: 'pago-sin-prestamo',
        severidad: 'error',
        descripcion: `Pago ${pago.numeroRecibo} (${pago.cliente}) no tiene préstamo asociado`,
        pagoId: pago.id,
        clienteNombre: pago.cliente,
      });
    }
  }

  return inconsistencias;
}

// ─── Motor de Riesgo Crediticio Interno ──────────────────────────────────────

export interface ConfiguracionRiesgo {
  pesoHistorialPagos: number;       // % del score (default 40)
  pesoDiasAtraso: number;           // % del score (default 25)
  pesoCapitalPendiente: number;     // % del score (default 15)
  pesoMora: number;                 // % del score (default 10)
  pesoRefinanciamientos: number;    // % del score (default 10)
  diasAtrasoAlto: number;           // días para considerar atraso alto (default 15)
  diasAtrasoCritico: number;        // días para considerar atraso crítico (default 30)
  maxPrestamosActivos: number;      // máximo préstamos activos sin penalización (default 2)
}

export const defaultConfigRiesgo: ConfiguracionRiesgo = {
  pesoHistorialPagos: 40,
  pesoDiasAtraso: 25,
  pesoCapitalPendiente: 15,
  pesoMora: 10,
  pesoRefinanciamientos: 10,
  diasAtrasoAlto: 15,
  diasAtrasoCritico: 30,
  maxPrestamosActivos: 2,
};

export function getConfiguracionRiesgo(): ConfiguracionRiesgo {
  if (typeof window === 'undefined') return defaultConfigRiesgo;
  const stored = localStorage.getItem('pm_config_riesgo');
  if (stored) {
    try { return { ...defaultConfigRiesgo, ...JSON.parse(stored) }; } catch {}
  }
  return defaultConfigRiesgo;
}

export function saveConfiguracionRiesgo(config: ConfiguracionRiesgo): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem('pm_config_riesgo', JSON.stringify(config));
  }
}

/**
 * calcularPerfilRiesgo — Motor de riesgo crediticio interno
 * Calcula score 0-100, nivel de riesgo, factores explicables y tendencia.
 * NO modifica datos financieros. Solo LEE, ANALIZA, CLASIFICA y REPORTA.
 */
export function calcularPerfilRiesgo(cliente: Cliente, prestamos: Prestamo[], pagos: Pago[]): PerfilRiesgo {
  const config = getConfiguracionRiesgo();
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const prestamoCliente = prestamos.filter(p => p.clienteId === cliente.id);
  const pagosCliente = pagos.filter(p => p.clienteId === cliente.id);

  // Sin historial
  if (prestamoCliente.length === 0) {
    return {
      clienteId: cliente.id,
      score: 100,
      nivel: 'sin-historial',
      factores: [],
      tendencia: 'estable',
      tendenciaDescripcion: 'Sin historial de préstamos',
      recomendacion: 'Cliente sin historial. Evaluar capacidad de pago antes de otorgar préstamo.',
      prestamosRealizados: 0,
      prestamosActivos: 0,
      prestamosPagados: 0,
      prestamosVencidos: 0,
      prestamosRefinanciados: 0,
      capitalPrestadoHistorico: 0,
      capitalRecuperado: 0,
      capitalPendiente: 0,
      interesRecuperado: 0,
      totalRecaudado: 0,
      moraTotal: 0,
      multaTotal: 0,
      pagosPuntuales: 0,
      pagosAtrasados: 0,
      pagosParciales: 0,
      mayorAtraso: 0,
      promedioAtraso: 0,
      ultimoPago: '',
      fechaCalculo: hoy.toISOString().slice(0, 10),
    };
  }

  // Estadísticas de préstamos
  const prestamosActivos = prestamoCliente.filter(p => p.estado === 'activo' || p.estado === 'mora').length;
  const prestamosPagados = prestamoCliente.filter(p => p.estado === 'pagado').length;
  const prestamosVencidos = prestamoCliente.filter(p => p.estado === 'vencido').length;
  const prestamosRefinanciados = prestamoCliente.filter(p => p.esRefinanciamiento).length;

  // Estadísticas financieras
  const stats = calcularEstadisticasFinancieras(prestamoCliente);

  // Análisis de cuotas
  let totalCuotas = 0;
  let cuotasPagadas = 0;
  let cuotasVencidas = 0;
  let cuotasParciales = 0;
  let diasAtrasoTotal = 0;
  let mayorAtraso = 0;
  let contadorAtraso = 0;

  for (const p of prestamoCliente) {
    for (const c of p.cuotas) {
      totalCuotas++;
      if (c.estado === 'pagado') cuotasPagadas++;
      else if (c.estado === 'vencido') {
        cuotasVencidas++;
        const [d, m, y] = c.fechaVencimiento.split('/').map(Number);
        const fechaCuota = new Date(y, m - 1, d);
        const dias = Math.max(0, Math.floor((hoy.getTime() - fechaCuota.getTime()) / (1000 * 60 * 60 * 24)));
        diasAtrasoTotal += dias;
        mayorAtraso = Math.max(mayorAtraso, dias);
        contadorAtraso++;
      } else if (c.estado === 'parcial') cuotasParciales++;
    }
  }

  const pagosPuntuales = cuotasPagadas;
  const pagosAtrasados = cuotasVencidas;
  const promedioAtraso = contadorAtraso > 0 ? Math.round(diasAtrasoTotal / contadorAtraso) : 0;

  // Último pago
  const ultimoPagoObj = pagosCliente.sort((a, b) => {
    const [da, ma, ya] = a.fecha.split('/').map(Number);
    const [db, mb, yb] = b.fecha.split('/').map(Number);
    return new Date(yb, mb - 1, db).getTime() - new Date(ya, ma - 1, da).getTime();
  })[0];
  const ultimoPago = ultimoPagoObj?.fecha || '';

  // ─── Cálculo del Score ────────────────────────────────────────────────────
  const factores: FactorRiesgo[] = [];
  let score = 100;

  // Factor 1: Historial de pagos (40 puntos)
  const porcentajePuntuales = totalCuotas > 0 ? (pagosPuntuales / totalCuotas) * 100 : 100;
  if (porcentajePuntuales < 100) {
    let penalizacion = Math.round(((100 - porcentajePuntuales) / 100) * config.pesoHistorialPagos);
    score -= penalizacion;
    if (penalizacion > 0) {
      factores.push({
        factor: 'Historial de pagos',
        descripcion: `${Math.round(porcentajePuntuales)}% de pagos puntuales`,
        impacto: penalizacion,
        valor: `${Math.round(porcentajePuntuales)}%`,
      });
    }
  }

  // Factor 2: Días de atraso (25 puntos)
  if (mayorAtraso > 0) {
    let penalizacion = 0;
    if (mayorAtraso >= config.diasAtrasoCritico) {
      penalizacion = config.pesoDiasAtraso;
    } else if (mayorAtraso >= config.diasAtrasoAlto) {
      penalizacion = Math.round(config.pesoDiasAtraso * 0.6);
    } else {
      penalizacion = Math.round(config.pesoDiasAtraso * 0.3);
    }
    score -= penalizacion;
    factores.push({
      factor: 'Días de atraso',
      descripcion: `Mayor atraso registrado: ${mayorAtraso} días`,
      impacto: penalizacion,
      valor: `${mayorAtraso} días`,
    });
  }

  // Factor 3: Capital pendiente (15 puntos)
  if (stats.capitalPendienteTotal > 0 && stats.capitalPrestadoTotal > 0) {
    const pctPendiente = stats.capitalPendienteTotal / stats.capitalPrestadoTotal;
    if (pctPendiente > 0.8) {
      let penalizacion = config.pesoCapitalPendiente;
      score -= penalizacion;
      factores.push({
        factor: 'Capital pendiente',
        descripcion: `${Math.round(pctPendiente * 100)}% del capital aún pendiente`,
        impacto: penalizacion,
        valor: `Lps ${r2(stats.capitalPendienteTotal).toLocaleString('es-HN', { minimumFractionDigits: 2 })}`,
      });
    }
  }

  // Factor 4: Mora (10 puntos)
  if (stats.moraPendienteTotal > 0) {
    let penalizacion = config.pesoMora;
    score -= penalizacion;
    factores.push({
      factor: 'Mora pendiente',
      descripcion: `Mora acumulada sin pagar`,
      impacto: penalizacion,
      valor: `Lps ${r2(stats.moraPendienteTotal).toLocaleString('es-HN', { minimumFractionDigits: 2 })}`,
    });
  }

  // Factor 5: Refinanciamientos (10 puntos)
  if (prestamosRefinanciados >= 2) {
    let penalizacion = Math.min(config.pesoRefinanciamientos, prestamosRefinanciados * 3);
    score -= penalizacion;
    factores.push({
      factor: 'Refinanciamientos',
      descripcion: `${prestamosRefinanciados} refinanciamientos realizados`,
      impacto: penalizacion,
      valor: prestamosRefinanciados,
    });
  }

  // Factor 6: Préstamos vencidos
  if (prestamosVencidos > 0) {
    let penalizacion = Math.min(15, prestamosVencidos * 8);
    score -= penalizacion;
    factores.push({
      factor: 'Préstamos vencidos',
      descripcion: `${prestamosVencidos} préstamo(s) en estado vencido`,
      impacto: penalizacion,
      valor: prestamosVencidos,
    });
  }

  // Factor 7: Pagos parciales
  if (cuotasParciales > 2) {
    let penalizacion = Math.min(10, cuotasParciales * 2);
    score -= penalizacion;
    factores.push({
      factor: 'Pagos parciales',
      descripcion: `${cuotasParciales} cuotas con pago parcial`,
      impacto: penalizacion,
      valor: cuotasParciales,
    });
  }

  score = Math.max(0, Math.min(100, score));
  const nivel = scoreToNivel(score, false);

  // ─── Tendencia ────────────────────────────────────────────────────────────
  // Comparar últimos 3 meses vs histórico
  const hace90dias = new Date(hoy);
  hace90dias.setDate(hace90dias.getDate() - 90);

  const pagosRecientes = pagosCliente.filter(p => {
    const [d, m, y] = p.fecha.split('/').map(Number);
    return new Date(y, m - 1, d) >= hace90dias;
  });

  const cuotasRecientes = prestamoCliente.flatMap(p => p.cuotas).filter(c => {
    const [d, m, y] = c.fechaVencimiento.split('/').map(Number);
    return new Date(y, m - 1, d) >= hace90dias;
  });

  const cuotasRecientesPagadas = cuotasRecientes.filter(c => c.estado === 'pagado').length;
  const pctReciente = cuotasRecientes.length > 0 ? (cuotasRecientesPagadas / cuotasRecientes.length) * 100 : 100;
  const pctHistorico = totalCuotas > 0 ? (pagosPuntuales / totalCuotas) * 100 : 100;

  let tendencia: 'mejorando' | 'estable' | 'empeorando';
  let tendenciaDescripcion: string;

  if (pctReciente > pctHistorico + 5) {
    tendencia = 'mejorando';
    tendenciaDescripcion = `Últimos 3 meses: ${Math.round(pctReciente)}% puntual vs histórico ${Math.round(pctHistorico)}%`;
  } else if (pctReciente < pctHistorico - 5) {
    tendencia = 'empeorando';
    tendenciaDescripcion = `Últimos 3 meses: ${Math.round(pctReciente)}% puntual vs histórico ${Math.round(pctHistorico)}%`;
  } else {
    tendencia = 'estable';
    tendenciaDescripcion = `Comportamiento estable: ${Math.round(pctHistorico)}% de pagos puntuales`;
  }

  // Recomendación
  let recomendacion = '';
  if (nivel === 'bajo') recomendacion = 'Cliente con excelente historial. Apto para nuevos préstamos.';
  else if (nivel === 'medio') recomendacion = 'Evaluar capacidad de pago antes de otorgar nuevo préstamo.';
  else if (nivel === 'alto') recomendacion = 'Requiere garantías adicionales. Verificar situación actual.';
  else if (nivel === 'critico') recomendacion = 'No se recomienda nuevo préstamo. Gestionar recuperación de cartera existente.';

  return {
    clienteId: cliente.id,
    score,
    nivel,
    factores,
    tendencia,
    tendenciaDescripcion,
    recomendacion,
    prestamosRealizados: prestamoCliente.length,
    prestamosActivos,
    prestamosPagados,
    prestamosVencidos,
    prestamosRefinanciados,
    capitalPrestadoHistorico: stats.capitalPrestadoTotal,
    capitalRecuperado: stats.capitalRecuperadoTotal,
    capitalPendiente: stats.capitalPendienteTotal,
    interesRecuperado: stats.interesRecuperadoTotal,
    totalRecaudado: stats.totalRecaudado,
    moraTotal: stats.moraPendienteTotal + stats.moraRecuperadaTotal,
    multaTotal: stats.multaPendienteTotal + stats.multaRecuperadaTotal,
    pagosPuntuales,
    pagosAtrasados,
    pagosParciales: cuotasParciales,
    mayorAtraso,
    promedioAtraso,
    ultimoPago,
    fechaCalculo: hoy.toISOString().slice(0, 10),
  };
}

// ─── Reversión de Pagos ───────────────────────────────────────────────────────

export interface ReversionPago {
  id: string;
  pagoOriginalId: string;
  numeroRecibo: string;
  prestamoId: string;
  clienteId: string;
  cliente: string;
  numeroCuota: number;
  montoOriginal: number;
  capitalOriginal: number;
  interesOriginal: number;
  servicioOriginal: number;
  moraOriginal: number;
  multaOriginal: number;
  motivo: string;
  usuario: string;
  fecha: string;
  hora: string;
}

const KEYS_REVERSION = 'pm_reversiones_pago';

export function getReversionesPago(): ReversionPago[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEYS_REVERSION);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveReversionesPago(data: ReversionPago[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(KEYS_REVERSION, JSON.stringify(data));
}

/**
 * revertirPago — Revierte un pago registrado
 * NUNCA elimina el pago original. Lo marca como revertido y recalcula saldos.
 * Registra auditoría completa.
 */
export function revertirPago(
  pagoId: string,
  motivo: string,
  usuario: string,
  prestamos: Prestamo[],
  pagos: Pago[]
): { success: boolean; error?: string; reversion?: ReversionPago } {
  const pago = pagos.find(p => p.id === pagoId);
  if (!pago) return { success: false, error: 'Pago no encontrado' };

  // Check if already reversed
  const reversiones = getReversionesPago();
  if (reversiones.some(r => r.pagoOriginalId === pagoId)) {
    return { success: false, error: 'Este pago ya fue revertido anteriormente' };
  }

  const prestamo = prestamos.find(p => p.id === pago.prestamoId);
  if (!prestamo) return { success: false, error: 'Préstamo no encontrado' };

  const now = new Date();
  const reversion: ReversionPago = {
    id: `rev-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    pagoOriginalId: pagoId,
    numeroRecibo: pago.numeroRecibo,
    prestamoId: pago.prestamoId,
    clienteId: pago.clienteId,
    cliente: pago.cliente,
    numeroCuota: pago.numeroCuota,
    montoOriginal: pago.total,
    capitalOriginal: pago.capital,
    interesOriginal: pago.interes,
    servicioOriginal: pago.servicioAdmin,
    moraOriginal: pago.mora,
    multaOriginal: pago.multa,
    motivo,
    usuario,
    fecha: `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`,
    hora: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
  };

  reversiones.push(reversion);
  saveReversionesPago(reversiones);

  return { success: true, reversion };
}

export function isPagoRevertido(pagoId: string): boolean {
  return getReversionesPago().some(r => r.pagoOriginalId === pagoId);
}

// ─── Estado de Cuenta por Cliente ────────────────────────────────────────────

export interface EstadoCuentaCliente {
  cliente: Cliente;
  prestamos: {
    prestamo: Prestamo;
    saldo: SaldoFinanciero;
    pagos: Pago[];
    cuotas: Cuota[];
  }[];
  resumen: {
    totalCapitalPrestado: number;
    totalCapitalRecuperado: number;
    totalCapitalPendiente: number;
    totalInteresRecuperado: number;
    totalServicioRecuperado: number;
    totalMoraRecuperada: number;
    totalMultaRecuperada: number;
    totalRecaudado: number;
    totalPendiente: number;
  };
}

export function generarEstadoCuenta(cliente: Cliente, prestamos: Prestamo[], pagos: Pago[]): EstadoCuentaCliente {
  const prestamoCliente = prestamos.filter(p => p.clienteId === cliente.id);
  const pagosCliente = pagos.filter(p => p.clienteId === cliente.id);

  const prestamosConSaldo = prestamoCliente.map(p => ({
    prestamo: p,
    saldo: calculateSaldoFinanciero(p),
    pagos: pagosCliente.filter(pg => pg.prestamoId === p.id),
    cuotas: p.cuotas,
  }));

  const resumen = {
    totalCapitalPrestado: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.capitalPrestado, 0)),
    totalCapitalRecuperado: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.capitalRecuperado, 0)),
    totalCapitalPendiente: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.capitalPendiente, 0)),
    totalInteresRecuperado: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.interesRecuperado, 0)),
    totalServicioRecuperado: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.servicioRecuperado, 0)),
    totalMoraRecuperada: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.moraRecuperada, 0)),
    totalMultaRecuperada: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.multaRecuperada, 0)),
    totalRecaudado: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.totalRecaudado, 0)),
    totalPendiente: r2(prestamosConSaldo.reduce((s, p) => s + p.saldo.totalPendiente, 0)),
  };

  return { cliente, prestamos: prestamosConSaldo, resumen };
}
